// Shared helpers for pet-related pages

export function timeAgoStr(iso, t) {
    if (!iso) return "";
    const then = new Date(iso).getTime();
    const diff = Math.max(0, Date.now() - then) / 1000;
    if (diff < 60) return t("dash.just_now");
    if (diff < 3600) return t("dash.min_ago", { n: Math.floor(diff / 60) });
    if (diff < 86400) return t("dash.hr_ago", { n: Math.floor(diff / 3600) });
    return t("dash.day_ago", { n: Math.floor(diff / 86400) });
}

// Canonical production site URL — always used to encode QR codes and share
// links so they resolve to https://getpawlink.com regardless of whether the
// user generated them from the preview URL, a PWA, or the production domain.
// Falls back to window.location.origin only if the env var is not set.
export const SITE_URL = (
    process.env.REACT_APP_SITE_URL ||
    (typeof window !== "undefined" ? window.location.origin : "")
).replace(/\/$/, "");
export const FRONTEND_BASE = SITE_URL;
export const finderUrl = (publicId) => `${SITE_URL}/p/${publicId}`;

/* ---------- Scan location helpers ----------
   A scan/notification carries either:
   - source = "gps": lat/lng from the finder's browser (+ optional accuracy in metres)
   - source = "ip":  IP-geolocated (city-level, always coarse)
   The owner needs to know EXACTLY which one they're looking at, so we
   classify every location into 3 buckets:
     "exact"   : GPS with accuracy <= 100m  → pin is right where the finder stood
     "coarse"  : GPS but accuracy > 100m    → real GPS, but WiFi/cell-tower fallback
     "approx"  : IP-only or missing coords  → city-level, could be km off
*/
export function scanLocation(scan) {
    const hasCoords = Number.isFinite(scan?.latitude) && Number.isFinite(scan?.longitude);
    const src = scan?.source;
    const acc = Number.isFinite(scan?.accuracy) ? scan.accuracy : null;
    let quality = "approx";
    if (src === "gps" && hasCoords) quality = (acc == null || acc <= 100) ? "exact" : "coarse";
    // IP-geolocated OR no source: always "approx" — never label as GPS.
    const mapHref = hasCoords
        ? `https://www.google.com/maps?q=${scan.latitude},${scan.longitude}`
        : scan?.city ? `https://www.google.com/maps?q=${encodeURIComponent(scan.city + (scan.country ? ", " + scan.country : ""))}` : null;
    return { hasCoords, quality, accuracy: acc, mapHref };
}

export function scanLocationLabel(scan, t) {
    const { quality, accuracy } = scanLocation(scan);
    if (quality === "exact") {
        return { key: "exact", text: accuracy != null ? t("scanloc.exact_acc", { m: Math.round(accuracy) }) : t("scanloc.exact") };
    }
    if (quality === "coarse") {
        return { key: "coarse", text: accuracy != null ? t("scanloc.coarse_acc", { m: Math.round(accuracy) }) : t("scanloc.coarse") };
    }
    return { key: "approx", text: t("scanloc.approx") };
}
